import Vue from 'vue';
import { User } from '@/store/modules/user';
import { SENTRY_VERSION } from '@/sentry-version';
import { get } from 'lodash';

const errorReportingEnv = process.env.WIZ_ENV; // use 'local-development' if needed
const errorReportingDsn = process.env.ERROR_REPORTING_DSN;
// const debugMode = process.env.NODE_ENV !== 'production';

// Only initialize when 1st error is caught (AppIndex > ErrorBoundary > SentryInitializer)
export async function initSentry(version?: string) {
  if (!errorReportingEnv || !errorReportingDsn) {
    return;
  }
  if (!version || version === '<SENTRY_VERSION_TO_REPLACE>') {
    return;
  }
  const checkedVersion = version;
  const { init } = await import('@sentry/vue');
  init({
    dsn: errorReportingDsn,
    environment: errorReportingEnv,
    release: checkedVersion,
    // debug: debugMode,
    Vue,
    attachProps: true,
    logErrors: true,
  });
}

// Identify Users
export async function configureSentryScopeUser(
  user: User | { loading: false },
) {
  if (errorReportingEnv == undefined || errorReportingDsn == undefined) {
    return;
  }
  const userId: string | undefined = (user as User).id;
  const userEmail: string | undefined = (user as User).email;
  const userObject =
    userId && userEmail
      ? {
          id: userId,
          email: userEmail,
        }
      : null;
  if (userObject !== null) {
    const { configureScope } = await import('@sentry/vue');
    configureScope((scope) => {
      scope.setUser(userObject);
    });
  } else {
    const { setUser } = await import('@sentry/vue');
    setUser(null);
  }
}

export async function onErrorCaptured(err: Error, vm: Vue) {
  if (noNeedToReportToSentry(err)) {
    return;
  }

  const { getCurrentHub, captureException } = await import('@sentry/vue');

  const client = getCurrentHub().getClient();
  if (!client) {
    await initSentry(SENTRY_VERSION);
  }
  await configureSentryScopeUser(vm.$store.state.user.currentUser);
  !!err && captureException(err);
}

/**
 * Determine if this error should be sent to Sentry.io
 */
function noNeedToReportToSentry(err: Error): boolean {
  const code = get(err, 'extensions.code');
  if (code === 'UNAUTHENTICATED') {
    // UNAUTHENTICATED is the custom code of `AuthenticationError` from "apollo-server-errors";
    return true;
  }

  return false;
}
